import React, { useCallback, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  WizardPhotoGalleryWrapper,
  LightboxOverlay,
  LightboxImage,
  LightboxCloseButton,
} from './WizardPhotoGallery.styled';

/**
 * Thumbnail grid for wizard request photos (review / summary only — no file names).
 * Clicking a thumbnail opens it full-size in an in-page lightbox.
 * @param {{ urls?: string[], items?: Array<{ src: string }>, inline?: boolean, className?: string }} props
 */
const WizardPhotoGallery = ({ urls, items, inline = false, className }) => {
  const [activeSrc, setActiveSrc] = useState(null);

  const srcList =
    items?.length > 0
      ? items.map((item) => item.src).filter((s) => typeof s === 'string' && s.trim())
      : (urls || []).filter((u) => typeof u === 'string' && u.trim()).map((u) => u.trim());

  const closeLightbox = useCallback(() => setActiveSrc(null), []);

  useEffect(() => {
    if (!activeSrc) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeSrc, closeLightbox]);

  if (!srcList.length) return null;

  return (
    <>
      <WizardPhotoGalleryWrapper className={className} $inline={inline}>
        <div className="photo-previews" role="list">
          {srcList.map((src, index) => (
            <button
              type="button"
              className="photo-thumb"
              role="listitem"
              key={`${src}-${index}`}
              onClick={() => setActiveSrc(src)}
              aria-label={`View photo ${index + 1} full size`}
            >
              <img src={src} alt={`Request upload ${index + 1}`} />
            </button>
          ))}
        </div>
      </WizardPhotoGalleryWrapper>

      {activeSrc && (
        <LightboxOverlay onClick={closeLightbox}>
          <LightboxCloseButton type="button" onClick={closeLightbox} aria-label="Close photo viewer">
            <CloseIcon />
          </LightboxCloseButton>
          <LightboxImage
            src={activeSrc}
            alt="Full size upload"
            onClick={(e) => e.stopPropagation()}
          />
        </LightboxOverlay>
      )}
    </>
  );
};

export default WizardPhotoGallery;
